import './styles.scss';

import { DeepPartial, DefaultValues, FormProvider, SubmitHandler, UseFormReturn, useForm } from 'react-hook-form';
import React, {
  FormEvent,
  HTMLAttributes,
  KeyboardEvent,
  RefAttributes,
  createContext,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { getClasses, getResultFromState } from '@/utils';

import FormButton from '@/common/Form/FormButton';
import FormCheckbox from '@/common/Form/FormCheckbox';
import FormField from '@/common/Form/FormField';
import FormInput from '@/common/Form/FormInput';
import FormSwitch from '@/common/Form/FormSwitch';
import FormWatch from '@/common/Form/FormWatch';
import { ZodType } from 'zod';
import { createComponentState } from '@/state';
import { zodResolver } from '@hookform/resolvers/zod';

export interface FormInterface {
  <InputsType>(props: FormProps<InputsType> & RefAttributes<UseFormReturn<InputsType>>): JSX.Element;
  displayName?: string;
  getStorage: (formName: string) => FormStateStorage[string];
  setStorage: (formName: string, input: any) => void;
  clearStorage: (formName: string) => void;
  Input: typeof FormInput;
  Button: typeof FormButton;
  Watch: typeof FormWatch;
  Checkbox: typeof FormCheckbox;
  Switch: typeof FormSwitch;
  Field: typeof FormField;
}
export type FormProps<T> = {
  name?: string;
  value?: T;
  onChange?: (value: DeepPartial<T>) => void;
  onSubmit: SubmitHandler<T>;
  validator?: ZodType<T, any, any>;
} & Omit<HTMLAttributes<HTMLFormElement>, 'onSubmit' | 'onChange'>;
type FormContextValue = {
  name?: string;
  validatingFields: [Set<string>, React.Dispatch<React.SetStateAction<Set<string>>>];
};
export const FormContext = createContext<FormContextValue>({
  name: undefined,
  validatingFields: [new Set(), () => undefined],
});

export type FormStateStorage = {
  [key: string]: Record<string, unknown>;
};
export const useFormStorage = createComponentState<FormStateStorage>({});
export const getFormStorage = (formName: string): FormStateStorage[string] => useFormStorage.getState()?.state?.[formName] ?? {};
export const setFormStorage = (formName: string, input) =>
  useFormStorage.setState((current) => ({
    ...current,
    state: { ...current.state, [formName]: getResultFromState(input, current?.state?.[formName] || {}) },
  }));
export const clearFormStorage = (formName: string): void =>
  useFormStorage.setState((current) => {
    const { state: { [formName]: _, ...state } = {}, ...rest } = current;
    return { ...rest, state };
  });

// eslint-disable-next-line react/display-name
const Form = forwardRef(
  <InputsType,>({ name, onSubmit, children, value, validator, onChange, ...formProps }: FormProps<InputsType>, ref): JSX.Element => {
    const validatingFields = useState<Set<string>>(new Set());

    const methods = useForm<InputsType>({
      defaultValues: value as DefaultValues<InputsType>,
      mode: 'onChange',
      reValidateMode: 'onChange',
      resolver: validator ? zodResolver<InputsType>(validator) : undefined,
    });

    useImperativeHandle(ref, () => methods, [methods]);
    const formContext = useMemo((): FormContextValue => ({ name, validatingFields }), [name, validatingFields]);

    useEffect(() => {
      if (!validator) return;
      methods.trigger();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
      event.preventDefault();
      event.stopPropagation();
      await methods.trigger();
      const isValid = Object.entries({ ...methods.formState.errors, ...methods.control._formState.errors }).reduce(
        (acc: boolean, [key, value]) => {
          if (validatingFields[0].has(key)) return acc && value === undefined;
          return acc;
        },
        true
      );
      if (!isValid) return;
      await onSubmit(methods.getValues(), event);
    };

    useEffect((): void => {
      if (!onChange) return;
      methods.watch((value: DeepPartial<InputsType>): void => onChange(value));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <FormProvider {...methods}>
        <FormContext.Provider value={formContext}>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
          <form
            {...formProps}
            className={getClasses('Form', formProps.className)}
            onSubmit={handleSubmit}
            onKeyDown={(event: KeyboardEvent<HTMLElement>): void => {
              event?.key === 'Enter' && !event?.shiftKey && event?.target['tagName'] !== 'TEXTAREA' ? event?.preventDefault?.() : undefined;
            }}
            autoComplete="off"
          >
            {children}
          </form>
        </FormContext.Provider>
      </FormProvider>
    );
  }
) as unknown as FormInterface;

Form.displayName = 'Form';
Form.getStorage = getFormStorage;
Form.setStorage = setFormStorage;
Form.clearStorage = clearFormStorage;
Form.Input = FormInput;
Form.Button = FormButton;
Form.Watch = FormWatch;
Form.Checkbox = FormCheckbox;
Form.Switch = FormSwitch;
Form.Field = FormField;

export default Form;
export { FormInput, FormButton, FormWatch, FormField };
